import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useTechnicalData from './hooks/useTechnicalData';
import { vehicleContext } from '../../VehicleProvider';
import Modal from '../../Modal';
import type { VehicleDetails } from '../../../types';
import LineInCircle from '../../../assets/icons/LineInCircleIcon';
import {
  Wrapper,
  Container,
  Row,
  SpecWrapper,
  SpecBodyWrapper,
  HeaderRow,
} from './TechnicalDataModal.styles';
import { Headline, Text } from '@oneaudi/unified-web-components';
import {
  SYS_SIZE_FIXED_200,
  SYS_SIZE_FIXED_400,
  SYS_SIZE_FIXED_500,
} from '@oneaudi/unified-web-common';

interface TechnicalDataModalProps {
  onClose: () => void;
}

const TechnicalDataModal: React.FC<TechnicalDataModalProps> = ({ onClose }) => {
  const { vehicle } = useContext(vehicleContext);
  const { t } = useTranslation();
  const technicalData = useTechnicalData();
  const [isModalContentFocused, setIsModalContentFocused] = useState(true);

  const { modelName, trimName } = vehicle as VehicleDetails;

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // pressing TAB key switches focus between `ModalContainer` and `CloseModalButton` components
      if (event.key === 'Tab') {
        event.preventDefault();
        if (isModalContentFocused) {
          document.getElementById('modalContainerVDPFSS')?.blur();
          document.getElementById('closeModalVDPFSS')?.focus();
          setIsModalContentFocused(false);
        } else {
          document.getElementById('closeModalVDPFSS')?.blur();
          document.getElementById('modalContainerVDPFSS')?.focus();
          setIsModalContentFocused(true);
        }
      }
    },
    [isModalContentFocused],
  );

  useEffect(() => {
    // focus the ModalContainer right after the Modal is painted on the screen
    document.getElementById('modalContainerVDPFSS')?.focus();
    return () => {
      // focus the InfoIcon component after closing the modal
      document.getElementById('infoIconVDPFSS')?.focus();
    };
  }, []);

  if (!vehicle) return null;

  return (
    <Modal onClose={onClose} onKeyDown={onKeyDown} data-testid="modal">
      <Wrapper>
        <Container>
          <Headline
            tag="h1"
            margin={[0, 0, SYS_SIZE_FIXED_500, 0]}
            variant={{
              order: '2',
              type: 'headline',
              weight: 'bold',
            }}
          >
            {t('featuresAndSpecsSection.categories.technicalData.title')}
          </Headline>
          <Headline
            tag="h2"
            margin={[0, 0, SYS_SIZE_FIXED_500, 0]}
            variant={{
              order: '3',
              type: 'headline',
              weight: 'bold',
            }}
            data-testid="modelName-trimName"
          >
            {modelName} {trimName}
          </Headline>
          {technicalData.map((section, i) => (
            <div key={i}>
              <HeaderRow>
                <Headline
                  data-testid="section-title"
                  tag="h2"
                  margin={[SYS_SIZE_FIXED_500, 0, SYS_SIZE_FIXED_500, 0]}
                  variant={{
                    order: '3',
                    type: 'headline',
                    weight: 'bold',
                  }}
                >
                  {section.title}
                </Headline>
              </HeaderRow>
              {section.categories.map((row, index) => (
                <Row key={index} finalRow={index === section.categories.length - 1}>
                  <SpecWrapper>
                    <Text
                      data-testid="row-spec"
                      margin={[SYS_SIZE_FIXED_200, 0, SYS_SIZE_FIXED_400, 0]}
                      tag="span"
                      variant={{
                        order: '1',
                        type: 'copy',
                        style: 'bold',
                      }}
                    >
                      {row.spec}
                    </Text>
                  </SpecWrapper>
                  <SpecBodyWrapper finalRow={index === section.categories.length - 1}>
                    {row.body ? (
                      <Text
                        data-testid="row-body"
                        tag="span"
                        variant={{
                          order: '1',
                          type: 'copy',
                          style: 'normal',
                        }}
                      >
                        {row.body}
                      </Text>
                    ) : (
                      <LineInCircle />
                    )}
                  </SpecBodyWrapper>
                </Row>
              ))}
            </div>
          ))}
        </Container>
      </Wrapper>
    </Modal>
  );
};

export default TechnicalDataModal;
